import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, Button, message, Space, Popconfirm, Descriptions, Form, Select, Input } from 'antd';
import { SettingOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import CdnDomainStatusTag from '../../../shared/components/CdnDomainStatusTag';
import { adminPath } from '../../../shared/config/paths';

export default function DomainSettings() {
  const { domainData, reloadDomain } = useOutletContext();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [errForm] = Form.useForm();
  const [errSaving, setErrSaving] = useState(false);
  const errMode = Form.useWatch('error_page_mode', errForm);

  useEffect(() => {
    if (!domainData) return;
    const raw = String(domainData.error_page_mode || 'passthrough');
    const mode = raw.startsWith('custom') ? 'custom' : raw === 'redirect' ? 'redirect' : 'passthrough';
    let settings = domainData.settings || {};
    if (typeof settings === 'string') {
      try { settings = JSON.parse(settings); } catch (e) { settings = {}; }
    }
    errForm.setFieldsValue({
      error_page_mode: mode,
      redirect_url: settings?.error_page?.redirect_url || '',
      redirect_status: settings?.error_page?.redirect_status || 302,
    });
  }, [domainData, errForm]);

  const saveErrorPage = async (values) => {
    setErrSaving(true);
    try {
      const payload = { id: domainData.id, error_page_mode: values.error_page_mode };
      if (values.error_page_mode === 'redirect') {
        payload.settings = { error_page: { redirect_url: values.redirect_url, redirect_status: values.redirect_status || 302 } };
      }
      const response = await cdnService.updateDomain(payload);
      if (response.success) {
        message.success('已保存');
        reloadDomain && reloadDomain();
      } else {
        message.error(response.error || '保存失败');
      }
    } catch (e) {
      message.error('保存失败');
    } finally {
      setErrSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await cdnService.deleteDomain(domainData.id);
      if (response.success) {
        message.success(`已删除 ${domainData.domain}`);
        navigate(adminPath('/cdn/domains'));
      } else {
        message.error(response.error || '删除失败');
      }
    } catch (error) {
      message.error('删除失败：' + (error?.message || 'unknown'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <Card title={<Space><SettingOutlined />域名设置</Space>}>
        <Descriptions bordered column={1} size="small" style={{ marginBottom: 24 }}>
          <Descriptions.Item label="域名">{domainData.domain}</Descriptions.Item>
          <Descriptions.Item label="所属用户">
            {domainData.user_email || domainData.user?.email || (domainData.user_id ? `#${domainData.user_id}` : '-')}
          </Descriptions.Item>
          <Descriptions.Item label="状态"><CdnDomainStatusTag status={domainData.status} /></Descriptions.Item>
          <Descriptions.Item label="创建时间">{domainData.created_at || '-'}</Descriptions.Item>
          {domainData.cloudflare_zone_id && (
            <Descriptions.Item label="Cloudflare Zone ID">{domainData.cloudflare_zone_id}</Descriptions.Item>
          )}
        </Descriptions>

        <Card type="inner" title="错误页面" style={{ marginBottom: 24 }}>
          <Form form={errForm} layout="vertical" onFinish={saveErrorPage} style={{ maxWidth: 480 }}>
            <Form.Item name="error_page_mode" label="源站返回 4xx/5xx 时">
              <Select
                options={[
                  { value: 'passthrough', label: '原样返回' },
                  { value: 'custom', label: '使用自定义错误页' },
                  { value: 'redirect', label: '跳转到指定地址' },
                ]}
              />
            </Form.Item>
            {errMode === 'custom' && (
              <div style={{ marginTop: -8, marginBottom: 16 }}>
                <Button type="link" style={{ padding: 0 }} onClick={() => navigate(adminPath(`/cdn/domains/${domainData.id}/error-pages`))}>管理错误页面</Button>
              </div>
            )}
            {errMode === 'redirect' && (
              <>
                <Form.Item
                  name="redirect_url"
                  label="跳转地址"
                  rules={[{ required: true, type: 'url', message: '请输入完整 URL，如 https://example.com/error' }]}
                >
                  <Input placeholder="https://example.com/error" />
                </Form.Item>
                <Form.Item name="redirect_status" label="跳转状态码">
                  <Select options={[301, 302, 303, 307, 308].map((c) => ({ value: c, label: c }))} />
                </Form.Item>
              </>
            )}
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={errSaving}>保存</Button>
          </Form>
        </Card>

        <Card type="inner" title="危险操作" style={{ borderColor: '#ff4d4f' }}>
          <Space direction="vertical">
            <span style={{ color: 'rgba(0,0,0,0.45)' }}>删除后源站、证书、规则等配置一并移除，不可恢复。</span>
            <Popconfirm
              title={`删除 ${domainData.domain}？`}
              description="此操作不可恢复"
              onConfirm={handleDelete}
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />} loading={deleting}>删除域名</Button>
            </Popconfirm>
          </Space>
        </Card>
      </Card>
    </div>
  );
}
